import { affirmSingleton } from "@ardoq/stub-implementation";
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useRef, useState, useCallback, useEffect, useId, Children, isValidElement, cloneElement } from "react";
import { noop, debounce } from "lodash";
import styled, { css } from "styled-components";
import { colors, shadowM, s8, s4 } from "@ardoq/design-tokens";
import { text2, popoverUnderline } from "@ardoq/typography";
import { useEffectOnce, useResizeObserver } from "@ardoq/hooks";
import { Icon, IconName } from "@ardoq/icons";
import { createRoot } from "react-dom/client";
import { FlexBox } from "@ardoq/layout";
const popoverRegistry = /* @__PURE__ */ new Map();
const popoverConfig = /* @__PURE__ */ new Map();
const POPOVER_ID_ATTR = "data-ardoq-popover-id";
const POPOVER_WRAPPER_CLASSNAME = "popover-wrapper";
const POPOVER_DELAY = 200;
const POPOVER_ARROW_SIZE = 10;
const POPOVER_Z_INDEX = 100002;
const VIEWPORT_PADDING = 10;
const MAX_POPOVER_HEIGHT_PERCENT = 0.75;
const POPOVER_WRAPPER_PADDING = 16;
var PopoverPlacement = /* @__PURE__ */ ((PopoverPlacement2) => {
  PopoverPlacement2["BOTTOM"] = "bottom";
  PopoverPlacement2["BOTTOM_LEFT"] = "bottom-left";
  PopoverPlacement2["BOTTOM_RIGHT"] = "bottom-right";
  PopoverPlacement2["TOP"] = "top";
  PopoverPlacement2["TOP_LEFT"] = "top-left";
  PopoverPlacement2["TOP_RIGHT"] = "top-right";
  return PopoverPlacement2;
})(PopoverPlacement || {});
const inIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};
const getTop = (targetBox, popoverBox, popoverPlacement) => {
  const scrollPosition = inIframe() ? 0 : window.scrollY;
  const topIncludingScrollPosition = targetBox.top + scrollPosition;
  return popoverPlacement === PopoverPlacement.TOP ? topIncludingScrollPosition - POPOVER_ARROW_SIZE - getMaxHeight(targetBox, popoverBox) : topIncludingScrollPosition + targetBox.height + POPOVER_ARROW_SIZE;
};
const getLeft = (targetBox) => {
  return targetBox.left + targetBox.width / 2;
};
const getMarginLeft = (targetBox, popoverBox) => {
  const left = getLeft(targetBox);
  const halfPopoverWidth = popoverBox.width / 2;
  if (left - halfPopoverWidth < VIEWPORT_PADDING) {
    return -left + VIEWPORT_PADDING;
  }
  if (left + halfPopoverWidth > window.innerWidth - VIEWPORT_PADDING) {
    return window.innerWidth - VIEWPORT_PADDING - popoverBox.width - left;
  }
  return -halfPopoverWidth;
};
const getMaxHeight = (targetBox, popoverBox) => {
  const maxPopoverHeight = window.innerHeight * MAX_POPOVER_HEIGHT_PERCENT;
  const popoverHeight = POPOVER_ARROW_SIZE + Math.min(popoverBox.height, maxPopoverHeight) - VIEWPORT_PADDING;
  const targetBottom = targetBox.top + targetBox.height;
  if (popoverHeight < targetBox.top || popoverHeight < window.innerHeight - targetBottom) {
    return popoverHeight;
  }
  return Math.max(targetBox.top, window.innerHeight - targetBottom) - POPOVER_ARROW_SIZE - VIEWPORT_PADDING;
};
const getBodyHeight = () => {
  const body = document.body;
  const html = document.documentElement;
  return Math.max(
    body.scrollHeight,
    body.offsetHeight,
    html.clientHeight,
    html.scrollHeight,
    html.offsetHeight
  );
};
const isBottomPlacementPossible = (targetBox, popoverBox) => {
  const popoverHeight = POPOVER_ARROW_SIZE + getMaxHeight(targetBox, popoverBox);
  if (targetBox.top !== void 0 && targetBox.top + targetBox.height + popoverHeight > window.innerHeight) {
    return false;
  }
  const bodyHeight = getBodyHeight();
  const targetBottom = targetBox.top + targetBox.height;
  return popoverHeight + targetBottom < bodyHeight;
};
const isTopPlacementPossible = (targetBox, popoverBox) => {
  const popoverHeight = POPOVER_ARROW_SIZE + getMaxHeight(targetBox, popoverBox);
  return targetBox.top - popoverHeight >= 0;
};
const bestPossiblePlacement = (targetBox) => targetBox.top > window.innerHeight - (targetBox.top + targetBox.height) ? PopoverPlacement.TOP : PopoverPlacement.BOTTOM;
const getPopoverPlacement = (targetBox, popoverBox, preferredPlacement) => {
  const unpreferredPlacement = preferredPlacement === PopoverPlacement.BOTTOM ? PopoverPlacement.TOP : PopoverPlacement.BOTTOM;
  const [isPreferredPlacementPossible, isUnpreferredPlacementPossible] = preferredPlacement === PopoverPlacement.BOTTOM ? [isBottomPlacementPossible, isTopPlacementPossible] : [isTopPlacementPossible, isBottomPlacementPossible];
  return isPreferredPlacementPossible(targetBox, popoverBox) ? preferredPlacement : isUnpreferredPlacementPossible(targetBox, popoverBox) ? unpreferredPlacement : bestPossiblePlacement(targetBox);
};
const getPopoverStyle = (targetBox, popoverBox, config) => {
  const { followsMouse, preferredPlacement = PopoverPlacement.BOTTOM } = config;
  const popoverPlacement = getPopoverPlacement(
    targetBox,
    popoverBox,
    preferredPlacement
  );
  return {
    className: popoverPlacement,
    style: {
      left: `${getLeft(targetBox)}px`,
      top: `${getTop(targetBox, popoverBox, popoverPlacement)}px`,
      width: followsMouse ? void 0 : 0
    }
  };
};
const getPopoverWrapperStyle = (targetBox, popoverBox, { followsMouse }) => {
  const maxPopoverHeight = getMaxHeight(targetBox, popoverBox);
  return {
    style: {
      marginLeft: `${getMarginLeft(targetBox, popoverBox)}px`,
      ...maxPopoverHeight < popoverBox.height ? { maxHeight: maxPopoverHeight, overflow: "auto" } : {},
      width: followsMouse ? void 0 : `${popoverBox.width}px`
    }
  };
};
const Popover$1 = styled.div`
  ${text2};
  color: ${colors.textDefault};
  z-index: ${POPOVER_Z_INDEX};
  position: absolute;
  left: -1000px;
  top: -1000px;
  ${/** don't show popover arrow when following mouse. */
({ $followsMouse }) => !$followsMouse && css`
        &:before {
          content: '';
          left: 50%;
          margin-left: -${POPOVER_ARROW_SIZE / 2}px;
          height: ${POPOVER_ARROW_SIZE}px;
          width: ${POPOVER_ARROW_SIZE}px;
          background: ${colors.surfaceDefault};
          position: absolute;
          pointer-events: none;
          box-shadow: -1px -1px 1px rgba(0, 0, 0, 0.1);
          transform: rotate(45deg);
        }
        &.${PopoverPlacement.TOP}:before {
          transform: rotate(-135deg);
          top: calc(100% - ${POPOVER_ARROW_SIZE / 2}px);
        }
        &.${PopoverPlacement.BOTTOM}:before {
          bottom: calc(100% - ${POPOVER_ARROW_SIZE / 2}px);
        }
      `}
  ${({ $acceptsMouse }) => !$acceptsMouse && css`
      user-select: none;
      pointer-events: none;
    `}
`;
const PopoverWrapper = styled.div`
  background: ${colors.surfaceDefault};
  box-shadow: ${shadowM};
  border-radius: ${s8};
  padding: ${POPOVER_WRAPPER_PADDING}px;
  overflow-wrap: break-word;
`;
const PopoverSection = styled.div`
  margin-top: 16px;
`;
const PlainTextPopover = styled.div`
  min-width: 120px;
  max-width: 272px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;
const Popover = styled(Popover$1)`
  transform: translate(-50%, ${POPOVER_ARROW_SIZE}px);
`;
const getStyle = (x, y, placement) => placement === PopoverPlacement.TOP ? {
  left: x,
  top: y,
  transform: `translate(-50%, calc(-100% - ${POPOVER_ARROW_SIZE}px))`
} : {
  left: x,
  top: y,
  transform: `translate(-50%, ${POPOVER_ARROW_SIZE}px)`
};
const PositionedPopover = ({
  position: [x, y],
  content,
  placement,
  acceptsMouse = false
}) => /* @__PURE__ */ jsx(
  Popover,
  {
    $followsMouse: false,
    $acceptsMouse: acceptsMouse,
    className: placement,
    style: getStyle(x, y, placement),
    children: /* @__PURE__ */ jsx(PopoverWrapper, { children: content })
  }
);
const POPOVER_MOUSE_FOLLOW_MARGIN = 25;
const startListening = (listener) => document.addEventListener("mouseover", listener, true);
const stopListening = (listener) => document.removeEventListener("mouseover", listener, true);
const startScrollListening = (listener) => document.addEventListener("scroll", listener, true);
const stopScrollListening = (listener) => document.removeEventListener("scroll", listener, true);
const startMouseMoveListening = (listener) => document.addEventListener("mousemove", listener, true);
const stopMouseMoveListening = (listener) => document.removeEventListener("mousemove", listener, true);
const throwNotMountedError = () => {
  throw new Error("Popover manager is not mounted yet.");
};
const registeredClosePopovers = [];
const closePopover = () => {
  if (!registeredClosePopovers.length) {
    throwNotMountedError();
  }
  registeredClosePopovers.forEach((entry) => entry());
};
const registeredShowPopovers = [];
const showPopoverAt = (args) => {
  if (!registeredShowPopovers.length) {
    throwNotMountedError();
  }
  const destroys = registeredShowPopovers.map((entry) => entry(args));
  return () => destroys.forEach((destroy) => destroy());
};
const useStateRef = (initialState) => {
  const [stateVariable, stateSetter] = useState(initialState);
  const ref = useRef(stateVariable);
  return [
    stateVariable,
    (value) => {
      stateSetter(value);
      ref.current = value;
    },
    ref
  ];
};
const PopoverManager = () => {
  const popoverRef = useRef(null);
  const targetRef = useRef(null);
  const [popoverStyle, setPopoverStyle] = useState();
  const [popoverWrapperStyle, setPopoverWrapperStyle] = useState();
  const [popoverContent, setPopoverContent] = useState();
  const [isVisible, setIsVisible] = useState(false);
  const [updatePositionTimeoutId, setUpdatePositionTimeoutId] = useState(0);
  const [eventPosition, setEventPosition] = useState(
    null
  );
  const [followsMouse, setFollowsMouse, followsMouseRef] = useStateRef(false);
  const [acceptsMouse, setAcceptsMouse] = useState(true);
  const [, setPreferredPlacement, preferredPlacementRef] = useStateRef(
    PopoverPlacement.BOTTOM
  );
  const updatePosition = useCallback(
    (mouseX, mouseY) => {
      var _a;
      const needsUpdate = !popoverStyle && isVisible && popoverRef.current || mouseX && mouseY && ((_a = popoverRef.current) == null ? void 0 : _a.childNodes.length);
      if (needsUpdate && targetRef.current && popoverRef.current) {
        const targetBox = mouseX && mouseY ? { left: mouseX, top: mouseY, width: 0, height: 0 } : eventPosition ? {
          left: eventPosition.clientX,
          top: eventPosition.clientY,
          width: 0,
          height: 0
        } : targetRef.current.getBoundingClientRect();
        const popoverBox = popoverRef.current.getBoundingClientRect();
        const hasContent = popoverBox.width > 2 * POPOVER_WRAPPER_PADDING;
        if (hasContent) {
          const config = {
            followsMouse: followsMouseRef.current,
            preferredPlacement: preferredPlacementRef.current
          };
          setPopoverStyle(getPopoverStyle(targetBox, popoverBox, config));
          setPopoverWrapperStyle(
            getPopoverWrapperStyle(targetBox, popoverBox, config)
          );
          setEventPosition(null);
          const visiblePosition = popoverBox.x >= 0 && popoverBox.y >= 0;
          if (!visiblePosition) {
            setUpdatePositionTimeoutId(
              window.setTimeout(() => updatePosition(mouseX, mouseY), 0)
            );
          }
        } else {
          setUpdatePositionTimeoutId(window.setTimeout(updatePosition, 16));
        }
      }
    },
    [
      popoverStyle,
      isVisible,
      eventPosition,
      followsMouseRef,
      preferredPlacementRef
    ]
  );
  const [positionedPopover, _setPositionedPopover] = useState(null);
  const positionedPopoverRef = useRef(positionedPopover);
  const onScrollListener = useRef(noop);
  const hidePopover = useCallback(() => {
    clearTimeout(updatePositionTimeoutId);
    setIsVisible(false);
    setPopoverContent(null);
    setPopoverStyle(null);
    setPopoverWrapperStyle(null);
    setEventPosition(null);
    stopScrollListening(onScrollListener.current);
  }, [updatePositionTimeoutId, onScrollListener]);
  const revealPopover = useCallback(
    (args) => {
      const setPositionedPopover = (value) => {
        _setPositionedPopover(value);
        positionedPopoverRef.current = value;
      };
      const returnFn = () => setPositionedPopover(null);
      hidePopover();
      setPositionedPopover(typeof args === "function" ? args(returnFn) : args);
      return returnFn;
    },
    [hidePopover, _setPositionedPopover]
  );
  const showPopover = (content) => {
    clearTimeout(updatePositionTimeoutId);
    setPopoverContent(content);
    setIsVisible(true);
    startScrollListening(onScrollListener.current);
  };
  useEffect(() => {
    onScrollListener.current = (e) => {
      var _a;
      const path = (_a = e.composedPath) == null ? void 0 : _a.call(e);
      if (popoverRef.current && path && !path.includes(popoverRef.current)) {
        hidePopover();
      }
    };
  }, [hidePopover]);
  useEffect(() => {
    if (!registeredClosePopovers.includes(hidePopover)) {
      registeredClosePopovers.push(hidePopover);
    }
    if (!registeredShowPopovers.includes(revealPopover)) {
      registeredShowPopovers.push(revealPopover);
    }
    return () => {
      const closeIndex = registeredClosePopovers.indexOf(hidePopover);
      if (closeIndex !== -1) {
        registeredClosePopovers.splice(closeIndex, 1);
      }
      const showIndex = registeredShowPopovers.indexOf(revealPopover);
      if (showIndex !== -1) {
        registeredShowPopovers.splice(showIndex, 1);
      }
    };
  }, [hidePopover, revealPopover]);
  useEffectOnce(() => {
    startListening(runPopoverShowHideLogic);
    startMouseMoveListening(handleMouseMove);
    return () => {
      clearTimeout(updatePositionTimeoutId);
      stopListening(runPopoverShowHideLogic);
      stopMouseMoveListening(handleMouseMove);
    };
  });
  const runPopoverShowHideLogic = debounce((event) => {
    var _a;
    runPopoverShowHideLogic.cancel();
    if (!(event.target instanceof Element)) {
      hidePopover();
      return;
    }
    const target = event.target.closest(`[${POPOVER_ID_ATTR}]`);
    if (!target) {
      const isHoveringPopover = (_a = popoverRef.current) == null ? void 0 : _a.contains(event.target);
      if (!isHoveringPopover) {
        hidePopover();
      }
      return;
    }
    const popoverId = target.getAttribute(POPOVER_ID_ATTR);
    if (!popoverId) {
      hidePopover();
      return;
    }
    if (!popoverRegistry.has(popoverId)) {
      hidePopover();
      return;
    }
    if (targetRef.current !== target) {
      hidePopover();
    }
    targetRef.current = target;
    const content = popoverRegistry.get(popoverId)(targetRef.current);
    if (!content) {
      hidePopover();
      return;
    }
    const config = popoverConfig.get(popoverId);
    setFollowsMouse((config == null ? void 0 : config.followsMouse) ?? false);
    setAcceptsMouse((config == null ? void 0 : config.acceptsMouse) ?? true);
    setPreferredPlacement(
      (config == null ? void 0 : config.preferredPlacement) ?? PopoverPlacement.BOTTOM
    );
    if (config == null ? void 0 : config.followsMouse) {
      setEventPosition({ clientX: event.clientX, clientY: event.clientY });
    }
    showPopover(content);
  }, POPOVER_DELAY);
  useEffect(() => {
    if (followsMouse && !eventPosition) {
      return;
    }
    updatePosition();
  }, [
    popoverStyle,
    isVisible,
    popoverWrapperStyle,
    updatePosition,
    followsMouse,
    eventPosition
  ]);
  const handleMouseMove = ({ clientX, clientY }) => {
    var _a;
    if (!followsMouseRef.current) {
      return;
    }
    updatePosition(clientX, clientY);
    const {
      left: targetX,
      top: targetY,
      right: targetRight,
      bottom: targetBottom
    } = ((_a = targetRef.current) == null ? void 0 : _a.getBoundingClientRect()) ?? {
      left: -Infinity,
      top: -Infinity,
      right: -Infinity,
      bottom: -Infinity
    };
    const xDistance = clientX >= targetX && clientX <= targetRight ? 0 : Math.min(
      Math.abs(clientX - targetX),
      Math.abs(clientX - targetRight)
    );
    const yDistance = clientY >= targetY && clientY <= targetBottom ? 0 : Math.min(
      Math.abs(clientY - targetY),
      Math.abs(clientY - targetBottom)
    );
    if (xDistance > POPOVER_MOUSE_FOLLOW_MARGIN || yDistance > POPOVER_MOUSE_FOLLOW_MARGIN) {
      setIsVisible(false);
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    isVisible && /* @__PURE__ */ jsx(
      Popover$1,
      {
        ref: popoverRef,
        ...popoverStyle,
        $followsMouse: followsMouse,
        $acceptsMouse: acceptsMouse,
        children: /* @__PURE__ */ jsx(
          PopoverWrapper,
          {
            className: POPOVER_WRAPPER_CLASSNAME,
            ...popoverWrapperStyle,
            children: popoverContent
          }
        )
      }
    ),
    positionedPopover && /* @__PURE__ */ jsx(PositionedPopover, { ...positionedPopover })
  ] });
};
const PLAIN_TEXT_POPOVER_ID = "plain-text-popover-id";
const LIST_POPOVER_ID = "list-popover-id";
const ListContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 120px;
  max-width: 240px;
`;
const ListItem = styled.div`
  display: flex;
  align-items: center;
  height: 30px;
`;
const SimpleTextPopover = ({ text }) => /* @__PURE__ */ jsx(PlainTextPopover, { children: text });
const plainTextPopoverRenderer = (target) => {
  var _a;
  const text = (_a = target == null ? void 0 : target.dataset) == null ? void 0 : _a.plainTextPopover;
  if (!text) return null;
  return /* @__PURE__ */ jsx(SimpleTextPopover, { text });
};
const isLast = (arr, el) => arr.length === arr.indexOf(el) + 1;
const listPopoverRenderer = (target) => {
  var _a;
  const listString = (_a = target == null ? void 0 : target.dataset) == null ? void 0 : _a.listPopover;
  if (!listString) return null;
  const list = listString.split(",");
  return /* @__PURE__ */ jsx(ListContent, { children: list.map((listElement, i) => /* @__PURE__ */ jsxs(ListItem, { children: [
    listElement,
    isLast(list, listElement) ? "" : ","
  ] }, i)) });
};
const registerTextPopovers = () => {
  popoverRegistry.set(PLAIN_TEXT_POPOVER_ID, plainTextPopoverRenderer);
  popoverRegistry.set(LIST_POPOVER_ID, listPopoverRenderer);
};
const withPlainTextPopover = (text) => {
  if (!text) return {};
  return {
    [POPOVER_ID_ATTR]: PLAIN_TEXT_POPOVER_ID,
    "data-plain-text-popover": text
  };
};
const usePopover = (renderer, options) => {
  const [popoverId] = useState(useId());
  const { followsMouse, acceptsMouse, preferredPlacement } = options || {};
  useEffect(() => {
    if (!renderer) {
      return;
    }
    popoverRegistry.set(popoverId, renderer);
    popoverConfig.set(popoverId, {
      followsMouse,
      acceptsMouse,
      preferredPlacement
    });
    return () => {
      popoverRegistry.delete(popoverId);
      popoverConfig.delete(popoverId);
    };
  }, [popoverId, renderer, followsMouse, acceptsMouse, preferredPlacement]);
  const popoProps = {
    [POPOVER_ID_ATTR]: popoverId
  };
  return popoProps;
};
const PopoveredText = styled.span`
  ${popoverUnderline}
`;
const WithPopover = ({
  content,
  followsMouse,
  acceptsMouse,
  preferredPlacement,
  children
}) => {
  const [prevContent, setPrevContent] = useState();
  const [popoverRenderer, setRenderer] = useState(null);
  if (content !== prevContent) {
    setPrevContent(() => content);
    if (typeof content === "string" || typeof content === "number") {
      setRenderer(() => () => /* @__PURE__ */ jsx(PlainTextPopover, { children: content }));
    } else if (content) {
      setRenderer(() => content);
    } else {
      setRenderer(null);
    }
  }
  const popoProps = usePopover(popoverRenderer, {
    followsMouse,
    acceptsMouse,
    preferredPlacement
  });
  if (!popoverRenderer) {
    return /* @__PURE__ */ jsx(Fragment, { children });
  }
  const kiddos = Children.map(children, (child) => {
    if (!child) {
      return;
    }
    if (isValidElement(child)) {
      return cloneElement(child, popoProps);
    }
    return /* @__PURE__ */ jsx(PopoveredText, { ...popoProps, children: child });
  });
  return /* @__PURE__ */ jsx(Fragment, { children: kiddos });
};
const WrapperWithEllipsisOverflow = styled.div`
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  /* If focusable, 4px padding is needed to display the focus ring */
  padding: ${(props) => props.$focusable ? s4 : 0};

  ${(props) => props.$lineClamp && css`
      display: -webkit-box;
      -webkit-line-clamp: ${props.$lineClamp};
      white-space: initial;
      overflow-wrap: break-word;
      -webkit-box-orient: vertical;
    `}
`;
const TextOverflowEllipsis = ({
  className,
  children,
  onEllipsisChange,
  onInnerTextChange,
  lineClamp,
  initialIsTextOverflowed,
  focusable,
  ...rest
}) => {
  const ref = useRef(null);
  const [isTextOverflowed, setIsTextOverflowed] = useState(
    initialIsTextOverflowed || false
  );
  const recalculateOverflow = useCallback(() => {
    const wrapperEl = ref.current;
    if (!wrapperEl) return;
    const { offsetWidth, offsetHeight, scrollWidth, scrollHeight } = wrapperEl;
    const currentHasEllipsisActive = lineClamp ? scrollHeight - offsetHeight > 9 : offsetWidth < scrollWidth;
    const innerText = wrapperEl.textContent ?? "";
    setIsTextOverflowed(currentHasEllipsisActive);
    if (onEllipsisChange && currentHasEllipsisActive !== isTextOverflowed) {
      onEllipsisChange(currentHasEllipsisActive, innerText);
    }
    if (onInnerTextChange) {
      onInnerTextChange(innerText);
    }
  }, [lineClamp, onEllipsisChange, onInnerTextChange, isTextOverflowed]);
  useEffect(recalculateOverflow, [recalculateOverflow]);
  useResizeObserver(ref, recalculateOverflow);
  return /* @__PURE__ */ jsx(
    WrapperWithEllipsisOverflow,
    {
      ref,
      className,
      $lineClamp: lineClamp,
      $focusable: focusable,
      ...rest,
      children
    }
  );
};
const TextOverflow = ({
  className,
  children,
  onEllipsisChange,
  lineClamp,
  renderIn = "popover",
  popoverFollowsMouse = false,
  popoverAcceptsMouse = true,
  textContent,
  focusable = false,
  ...rest
}) => {
  const showPopover = renderIn === "popover";
  const showTooltip = renderIn === "tooltip";
  const [isTextOverflowed, setIsTextOverflowed] = useState(false);
  const [innerText, setInnerText] = useState("");
  const handleEllipsisChange = useCallback(
    (overflowed, innerText2) => {
      const content = textContent ?? innerText2;
      setIsTextOverflowed(overflowed);
      setInnerText(content);
      if (onEllipsisChange && overflowed !== isTextOverflowed) {
        onEllipsisChange(overflowed);
      }
    },
    [textContent, onEllipsisChange, isTextOverflowed]
  );
  const handleInnerTextChange = useCallback(
    (innerText2) => {
      const content = textContent ?? innerText2;
      setInnerText(content);
    },
    [textContent]
  );
  const ellipsisProps = {
    className,
    lineClamp,
    onEllipsisChange: handleEllipsisChange,
    onInnerTextChange: handleInnerTextChange,
    ...rest
  };
  return /* @__PURE__ */ jsx(
    WithPopover,
    {
      followsMouse: popoverFollowsMouse,
      acceptsMouse: popoverAcceptsMouse,
      content: showPopover && isTextOverflowed && innerText,
      children: /* @__PURE__ */ jsx(
        TextOverflowEllipsis,
        {
          ...showTooltip && isTextOverflowed ? { "data-tooltip-text": innerText } : {},
          ...ellipsisProps,
          initialIsTextOverflowed: isTextOverflowed,
          focusable,
          children
        }
      )
    }
  );
};
const getRenderedWidth = (element) => new Promise((resolve) => {
  const offscreenRender = document.createElement("div");
  offscreenRender.style.position = "absolute";
  offscreenRender.style.visibility = "hidden";
  offscreenRender.style.height = "auto";
  offscreenRender.style.width = "auto";
  document.body.appendChild(offscreenRender);
  const root = createRoot(offscreenRender);
  root.render(element);
  setTimeout(() => {
    const width = offscreenRender.offsetWidth;
    root.unmount();
    document.body.removeChild(offscreenRender);
    resolve(width);
  }, 0);
});
const decorateReactElement = async (element) => {
  const clone = cloneElement(element);
  const width = await getRenderedWidth(clone);
  return { element, width };
};
const getDecoratedReactElements = (nodes) => Promise.all(
  Children.toArray(nodes).filter(isValidElement).map(decorateReactElement)
);
const initState = (state) => ({
  visible: [],
  overflow: [],
  containerWidth: 0,
  gapWidth: 0,
  currentWidth: 0,
  ...state
});
const accumulateElementByVisibility = ({
  visible,
  overflow,
  containerWidth,
  gapWidth,
  currentWidth: _currentWidth
}, { element, width }) => {
  let currentWidth = _currentWidth;
  if (containerWidth - currentWidth - gapWidth - width > 0) {
    visible.push(element);
    currentWidth += width + gapWidth;
  } else {
    overflow.push(element);
  }
  return { visible, overflow, containerWidth, gapWidth, currentWidth };
};
const distributeElementsByVisibility = (state, elements) => elements.reduce(accumulateElementByVisibility, state);
const layoutCalculationStateOperations = {
  initState,
  distributeElementsByVisibility
};
const overflowContainerGap = s4;
const ellipsesIconWidth = 24;
const OverflowContainer = styled.div`
  display: flex;
  overflow: hidden;
  position: relative;
  gap: ${overflowContainerGap};
`;
const RightAlignedBox = styled.div`
  margin-left: auto;
`;
const calculateAndDistributeChildren = async (children, containerWidth, setVisibleChildren, setOverflowChildren) => {
  const decoratedChildren = await getDecoratedReactElements(children);
  const initialState = layoutCalculationStateOperations.initState({
    containerWidth,
    gapWidth: parseInt(overflowContainerGap, 10),
    currentWidth: ellipsesIconWidth
  });
  const { visible, overflow } = layoutCalculationStateOperations.distributeElementsByVisibility(
    initialState,
    decoratedChildren
  );
  setVisibleChildren(visible);
  setOverflowChildren(overflow);
};
const ListWithPopover = ({ children }) => {
  const containerRef = useRef(null);
  const [visibleChildren, setVisibleChildren] = useState([]);
  const [overflowChildren, setOverflowChildren] = useState([]);
  useEffect(() => {
    var _a;
    calculateAndDistributeChildren(
      children,
      ((_a = containerRef.current) == null ? void 0 : _a.offsetWidth) ?? 0,
      setVisibleChildren,
      setOverflowChildren
    );
  }, [children]);
  return /* @__PURE__ */ jsxs(OverflowContainer, { ref: containerRef, children: [
    visibleChildren,
    overflowChildren.length > 0 && /* @__PURE__ */ jsx(RightAlignedBox, { children: /* @__PURE__ */ jsx(
      WithPopover,
      {
        content: () => /* @__PURE__ */ jsx(FlexBox, { gap: "xxsmall", children: overflowChildren }),
        children: /* @__PURE__ */ jsx(Icon, { iconName: IconName.MORE_HORIZ })
      }
    ) })
  ] });
};
affirmSingleton("@ardoq/popovers");
export {
  ListItem,
  ListWithPopover,
  POPOVER_ID_ATTR,
  POPOVER_WRAPPER_CLASSNAME,
  POPOVER_WRAPPER_PADDING,
  POPOVER_Z_INDEX,
  PlainTextPopover,
  Popover$1 as Popover,
  PopoverManager,
  PopoverPlacement,
  PopoverSection,
  PopoverWrapper,
  SimpleTextPopover,
  TextOverflow,
  TextOverflowEllipsis,
  WithPopover,
  closePopover,
  popoverConfig,
  popoverRegistry,
  registerTextPopovers,
  showPopoverAt,
  usePopover,
  withPlainTextPopover
};
//# sourceMappingURL=index.js.map
